/**
 * Per-client Phones tab.
 *
 * Aggregates every communications channel attached to this client in one
 * place:
 *   1. Twilio numbers — assigned via the master `/integrations/twilio`
 *      page (Client + Description of Use fields). Source of truth lives
 *      in `db.twilio_number_assignments`.
 *   2. 3CX extensions — *placeholder*. Once the Call Control API +
 *      Web Client iframe integration is wired up, this section will
 *      list each extension, the person it belongs to, and the
 *      external number (mobile / fixed) it routes to. The visual frame
 *      is already here so admins can see where the data will land.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Phone, RefreshCw, Loader2, AlertTriangle, ExternalLink, Copy,
  PhoneForwarded, Construction, Voicemail, Mail,
} from "lucide-react";

const CAP_LABELS = { voice: "Voice", sms: "SMS", mms: "MMS", fax: "Fax" };

export default function ClientPhonesPage() {
  const { clientId } = useParams();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [twilioRows, setTwilioRows] = useState([]);
  const [threecxRows, setThreecxRows] = useState([]);
  const [voicemailRows, setVoicemailRows] = useState([]);
  const [voicemailError, setVoicemailError] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const [tw, cx, vm] = await Promise.all([
        api.get(`/clients/${clientId}/twilio/numbers`)
          .then((r) => r.data?.numbers || [])
          .catch(() => []),
        api.get(`/clients/${clientId}/threecx/extensions`)
          .then((r) => r.data?.extensions || [])
          .catch(() => []),
        api.get(`/clients/${clientId}/threecx/voicemails`)
          .then((r) => ({
            voicemails: r.data?.voicemails || [],
            pbx_error: r.data?.pbx_error || null,
          }))
          .catch(() => ({ voicemails: [], pbx_error: null })),
      ]);
      setTwilioRows(tw);
      setThreecxRows(cx);
      setVoicemailRows(vm.voicemails);
      setVoicemailError(vm.pbx_error);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load phones");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const onRefresh = async () => {
    setRefreshing(true);
    await load({ silent: true });
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied");
    } catch (_) {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="client-phones-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-overline">Connections · Phones</div>
          <h1 className="font-display font-extrabold text-4xl tracking-tight">
            Phones
          </h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-3xl">
            Every inbound + outbound voice and messaging channel attached to
            this client — Twilio DIDs at the top, 3CX extensions and call
            routing underneath. Assignments are managed centrally; this page
            is the single source of truth for &quot;which numbers ring on this
            client&apos;s phones?&quot;
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={onRefresh}
          disabled={refreshing}
          className="rounded-sm" data-testid="phones-refresh">
          {refreshing
            ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
          Refresh
        </Button>
      </div>

      {error && (
        <div className="rounded-sm border border-red-200 bg-red-50 p-3 text-sm text-red-900 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-red-700 mt-0.5 shrink-0" />
          {error}
        </div>
      )}

      {/* ── Twilio numbers ─────────────────────────────────────────── */}
      <Card className="rounded-sm border-zinc-200 overflow-hidden">
        <div className="h-1.5" style={{ background: "#F22F46" }} />
        <CardContent className="p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-sm flex items-center justify-center"
              style={{ background: "#F22F4614" }}>
              <Phone className="h-5 w-5 text-[#F22F46]" />
            </div>
            <div>
              <h2 className="font-display font-bold text-xl">
                Twilio numbers ({twilioRows.length})
              </h2>
              <p className="text-[12px] text-zinc-500 mt-0.5 max-w-2xl">
                Public DIDs that ring into this client. Need a new line, or
                want to change which DID is assigned? Ask an administrator —
                assignments are managed on{" "}
                <span className="font-mono">/integrations/twilio</span>.
              </p>
            </div>
          </div>

          {loading ? (
            <div className="text-[12px] text-zinc-500 flex items-center gap-2 py-3">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading numbers…
            </div>
          ) : twilioRows.length === 0 ? (
            <div className="rounded-sm border border-dashed border-zinc-200 bg-zinc-50/40 p-6 text-center text-[12px] text-zinc-600"
              data-testid="twilio-phones-empty">
              No Twilio numbers are assigned to this client yet.
            </div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {twilioRows.map((n) => (
                <TwilioRow key={n.sid} n={n} onCopy={copy} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── 3CX extensions (placeholder) ────────────────────────────── */}
      <Card className="rounded-sm border-zinc-200 overflow-hidden">
        <div className="h-1.5" style={{ background: "#00BCD4" }} />
        <CardContent className="p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-sm flex items-center justify-center"
              style={{ background: "#00BCD414" }}>
              <PhoneForwarded className="h-5 w-5 text-[#00BCD4]" />
            </div>
            <div>
              <h2 className="font-display font-bold text-xl">
                3CX extensions ({threecxRows.length})
              </h2>
              <p className="text-[12px] text-zinc-500 mt-0.5 max-w-2xl">
                Internal extensions (e.g. <span className="font-mono">101</span>{" "}
                — Sales Manager) and where each one routes to (e.g. forward to
                a personal mobile, voicemail, ring-group, IVR menu).
              </p>
            </div>
          </div>

          {loading ? (
            <div className="text-[12px] text-zinc-500 flex items-center gap-2 py-3">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading extensions…
            </div>
          ) : threecxRows.length === 0 ? (
            <div className="rounded-sm border border-dashed border-zinc-200 bg-zinc-50/40 p-5 space-y-2"
              data-testid="threecx-extensions-placeholder">
              <div className="flex items-center gap-2 text-[12px] text-zinc-700 font-semibold">
                <Construction className="h-3.5 w-3.5 text-amber-600" />
                No 3CX extensions assigned to this client yet
              </div>
              <p className="text-[11px] text-zinc-500 leading-relaxed">
                Open <span className="font-mono">/integrations/3cx/call-centre</span>{" "}
                → <em>Client assignments</em> to tag each PBX extension with the
                client it serves, the person who owns it, and the external
                number it routes to.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {threecxRows.map((e) => (
                <ThreeCxRow key={e.extension_id || e.extension_number} e={e}
                  onCopy={copy} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Voicemail audio files ───────────────────────────────────── */}
      <Card className="rounded-sm border-zinc-200 overflow-hidden">
        <div className="h-1.5" style={{ background: "#7C3AED" }} />
        <CardContent className="p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-sm flex items-center justify-center"
              style={{ background: "#7C3AED14" }}>
              <Voicemail className="h-5 w-5 text-[#7C3AED]" />
            </div>
            <div>
              <h2 className="font-display font-bold text-xl">
                Voicemails ({voicemailRows.length})
              </h2>
              <p className="text-[12px] text-zinc-500 mt-0.5 max-w-2xl">
                Every voicemail audio file received on an extension owned by
                this client. Auto-tagged from the extension assignments above;
                admins can pin individual messages to a different client from
                the master Call Centre page.
              </p>
            </div>
          </div>

          {loading ? (
            <div className="text-[12px] text-zinc-500 flex items-center gap-2 py-3">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading voicemails…
            </div>
          ) : voicemailRows.length === 0 ? (
            <div className="rounded-sm border border-dashed border-zinc-200 bg-zinc-50/40 p-4 text-[12px] text-zinc-600"
              data-testid="threecx-voicemails-empty">
              {voicemailError
                ? <span className="text-amber-800">{voicemailError}</span>
                : "No voicemails yet for this client's assigned extensions."}
            </div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {voicemailRows.map((v) => (
                <VoicemailRow key={v.id} v={v} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function TwilioRow({ n, onCopy }) {
  const caps = n.capabilities || {};
  return (
    <div className="py-3 first:pt-0 last:pb-0 grid grid-cols-1 md:grid-cols-[220px_1fr] gap-4"
      data-testid={`twilio-phone-${n.sid}`}>
      <div>
        <div className="flex items-center gap-2">
          <span className="font-mono text-base font-semibold tracking-tight">
            {n.phone_number || "—"}
          </span>
          {n.phone_number && (
            <button type="button" onClick={() => onCopy(n.phone_number)}
              className="text-zinc-400 hover:text-[#F22F46] transition-colors"
              title="Copy phone number">
              <Copy className="h-3 w-3" />
            </button>
          )}
        </div>
        {n.friendly_name && (
          <div className="text-[11px] font-mono text-zinc-500 mt-0.5 truncate"
            title={n.friendly_name}>
            {n.friendly_name}
          </div>
        )}
        <div className="mt-1.5 flex flex-wrap gap-1">
          {["voice", "sms", "mms", "fax"]
            .filter((k) => caps[k])
            .map((k) => (
              <Badge key={k} variant="outline"
                className="rounded-sm text-[9px] font-display font-bold uppercase tracking-[0.15em] bg-zinc-50">
                {CAP_LABELS[k]}
              </Badge>
            ))}
        </div>
      </div>
      <div className="space-y-1.5">
        <div>
          <div className="label-overline text-[9px] mb-0.5">Description of use</div>
          {n.description ? (
            <div className="text-sm text-zinc-800">{n.description}</div>
          ) : (
            <div className="text-[12px] italic text-zinc-400">
              No description provided.
            </div>
          )}
        </div>
        {(n.voice_url || n.sms_url) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 pt-1.5 border-t border-zinc-100 mt-1.5">
            {n.voice_url && (
              <div className="text-[11px] font-mono text-zinc-600 truncate"
                title={n.voice_url}>
                <span className="label-overline text-[9px] mr-1.5">Voice</span>
                <a href={n.voice_url} target="_blank" rel="noreferrer"
                  className="hover:underline inline-flex items-center gap-1">
                  {n.voice_url}
                  <ExternalLink className="h-2.5 w-2.5" />
                </a>
              </div>
            )}
            {n.sms_url && (
              <div className="text-[11px] font-mono text-zinc-600 truncate"
                title={n.sms_url}>
                <span className="label-overline text-[9px] mr-1.5">SMS</span>
                <a href={n.sms_url} target="_blank" rel="noreferrer"
                  className="hover:underline inline-flex items-center gap-1">
                  {n.sms_url}
                  <ExternalLink className="h-2.5 w-2.5" />
                </a>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ThreeCxRow({ e, onCopy }) {
  return (
    <div className="py-3 first:pt-0 last:pb-0 grid grid-cols-1 md:grid-cols-[140px_1fr_220px] gap-4 items-start"
      data-testid={`threecx-ext-${e.extension_number || e.extension_id}`}>
      <div>
        <div className="font-mono text-base font-semibold tracking-tight">
          Ext {e.extension_number || e.extension_id}
        </div>
      </div>
      <div>
        <div className="label-overline text-[9px] mb-0.5">Assigned to</div>
        <div className="text-sm font-semibold text-zinc-900">
          {e.name || <span className="italic text-zinc-400">Unassigned</span>}
        </div>
        {e.description && (
          <div className="text-[12px] text-zinc-600 mt-0.5">{e.description}</div>
        )}
      </div>
      <div>
        <div className="label-overline text-[9px] mb-0.5">Routes to</div>
        {e.routes_to ? (
          <div className="flex items-center gap-1.5">
            <PhoneForwarded className="h-3 w-3 text-[#00BCD4] shrink-0" />
            <span className="font-mono text-[12px] text-zinc-800">{e.routes_to}</span>
            <button type="button" onClick={() => onCopy(e.routes_to)}
              className="text-zinc-400 hover:text-[#00BCD4] transition-colors"
              title="Copy destination">
              <Copy className="h-3 w-3" />
            </button>
          </div>
        ) : (
          <div className="text-[12px] italic text-zinc-400">
            Voicemail / no forward
          </div>
        )}
      </div>
    </div>
  );
}

function VoicemailRow({ v }) {
  const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const received = (v.received_time || "").slice(0, 19).replace("T", " ");
  return (
    <div className="py-3 first:pt-0 last:pb-0 grid grid-cols-1 md:grid-cols-[160px_140px_120px_1fr] gap-3 text-xs items-start"
      data-testid={`vm-${v.id}`}>
      <span className="font-mono text-[11px] text-zinc-600">
        {received || "—"}
      </span>
      <span className="font-mono text-[11px] truncate" title={v.caller}>
        <Voicemail className="h-3 w-3 inline mr-1 text-[#7C3AED]" />
        {v.caller || "Unknown caller"}
      </span>
      <span className="font-mono text-[11px] text-zinc-600 truncate">
        {v.person_name
          ? <>{v.person_name}</>
          : v.owner_extension
          ? <>ext {v.owner_extension}</>
          : <span className="italic text-zinc-400">—</span>}
      </span>
      <div className="space-y-1 min-w-0">
        {!v.stale ? (
          <audio controls preload="none"
            src={`${API_BASE}${v.download_url}`}
            className="w-full h-8"
            data-testid={`vm-audio-${v.id}`} />
        ) : (
          <div className="text-[11px] italic text-zinc-500">
            Audio no longer available on the PBX.
          </div>
        )}
        <div className="flex items-center gap-1.5 flex-wrap text-[10px]">
          {v.assignment_source === "override" && (
            <span className="rounded-sm bg-amber-50 text-amber-800 border border-amber-200 px-1 py-0.5 font-mono">
              manual override
            </span>
          )}
          {v.assignment_source === "extension" && (
            <span className="rounded-sm bg-emerald-50 text-emerald-800 border border-emerald-200 px-1 py-0.5 font-mono">
              auto-tagged
            </span>
          )}
          {v.emailed && (
            <span className="inline-flex items-center gap-0.5 rounded-sm bg-emerald-50 text-emerald-800 border border-emerald-200 px-1 py-0.5 font-mono">
              <Mail className="h-2.5 w-2.5" /> emailed
            </span>
          )}
          {v.transcription && (
            <span className="text-zinc-600 italic line-clamp-1" title={v.transcription}>
              &quot;{v.transcription}&quot;
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
